import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { MygaRateSheetStateService } from '../state/myga-rate-sheet-state.service';
import { SessionContextService } from '../../../core/services/session-context.service';
import { RateSheetTilesComponent } from './shared/rate-sheet-tiles.component';
import { RateSheetFiltersComponent } from './shared/rate-sheet-filters.component';
import { RateSheetChangesComponent } from './shared/rate-sheet-changes.component';

/**
 * Left rail for the MYGA + Standard Fixed Rate Sheets (/rate-sheet,
 * /standard-fixed-rate-sheet). Sheet tiles + Scope (shelf) + Filters form +
 * My Views. The same component serves both sheets — surface key + term label
 * are derived from the route so saved views and the Term label stay sheet-specific.
 */
@Component({
  selector: 'app-myga-rate-sheet-rail',
  standalone: true,
  imports: [CommonModule, RateSheetTilesComponent, RateSheetFiltersComponent, RateSheetChangesComponent],
  template: `
    <div class="rail-content">
      <!-- Sheet selector tiles -->
      <app-rate-sheet-tiles />

      <!-- Scope -->
      <div class="rgrp">
        <div class="rgrp-h">Scope</div>
        <div class="knob">
          <span class="klabel">Products</span>
          <div class="seg"
               [class.seg-locked]="shelfState() === 'locked'"
               [class.seg-disabled]="shelfState() === 'disabled'"
               [attr.title]="shelfState() === 'locked' ? 'Your account is restricted to shelf products'
                             : shelfState() === 'disabled' ? 'Select a user via Admin to enable'
                             : 'Toggle between all products and your shelf'">
            <span class="seg-opt" [class.on]="shelfState() === 'off'" (click)="onShelfClick()">All</span>
            <span class="seg-opt" [class.on]="shelfState() === 'on' || shelfState() === 'locked'"
                  [class.forced]="shelfState() === 'locked'" (click)="onShelfClick()">My Shelf</span>
          </div>
        </div>
        <app-rate-sheet-changes [state]="state" />
      </div>

      <!-- Filters -->
      <app-rate-sheet-filters
        [state]="state"
        [showTax]="true"
        [termLabel]="termLabel()" />
    </div>
  `,
  styles: [`
    :host { display: block; height: 100%; }
    .rail-content { display: flex; flex-direction: column; }
    .rgrp { border-bottom: 1px solid var(--border-light); padding: 10px 14px 12px; }
    .rgrp-h { font-size: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--muted); margin-bottom: 9px; }
    .knob { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: 8px 0; }
    .klabel { font-size: 12px; color: var(--text2); font-weight: 600; }
    .seg { display: inline-flex; border: 1px solid var(--border); border-radius: 7px; overflow: hidden; }
    .seg-opt { padding: 5px 11px; font-size: 12px; font-weight: 600; color: var(--muted); cursor: pointer; }
    .seg-opt.on { background: var(--navy); color: #fff; }
    .seg-opt.forced { cursor: default; }
    .seg-disabled { opacity: .5; pointer-events: none; }
    .seg-locked .seg-opt:not(.on) { cursor: default; }
  `],
})
export class MygaRateSheetRailComponent {
  protected readonly state = inject(MygaRateSheetStateService);
  protected readonly sessionCtx = inject(SessionContextService);
  private readonly router = inject(Router);

  private readonly url = signal(this.router.url);
  protected readonly isStandard = computed(() => this.url().includes('standard-fixed'));
  protected readonly termLabel = computed(() => this.isStandard() ? 'Surrender term (yr)' : 'Guarantee term (yr)');

  protected readonly shelfState = computed<'off' | 'on' | 'locked' | 'disabled'>(() => {
    if (!this.sessionCtx.hasSession()) return 'disabled';
    if (this.sessionCtx.shouldForceShelf('FA')) return 'locked';
    return this.sessionCtx.shelfMode() ? 'on' : 'off';
  });

  constructor() {
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(),
    ).subscribe(e => this.url.set(e.urlAfterRedirects));
  }

  onShelfClick(): void {
    const s = this.shelfState();
    if (s === 'locked' || s === 'disabled') return;
    this.sessionCtx.toggleShelfMode();
  }
}
